/**
 * RoleChooser — role selection screen for multi-role users.
 *
 * Shown after login when the user's email can operate as either
 * Adviser or Assistant. The chosen role is persisted in sessionStorage.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Inbox, ClipboardList } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { getRoleLabel } from '../auth/authConfig';

const EMAIL_TO_NAME = {
  'marinus@optimumgroup.co.za': 'Marinus',
  'wlourns@gmail.com': 'Wouter',
  'bridgesvix@gmail.com': 'Vicky',
};

function getFirstName(email) {
  return EMAIL_TO_NAME[(email || '').toLowerCase()] || 'there';
}

export default function RoleChooser() {
  const { user, selectRole, availableRoles } = useAuth();
  const firstName = getFirstName(user?.email);
  const roles = availableRoles || [];
  const roleCount = roles.length;
  const isSingleRole = roleCount === 1;
  // Column count tracks the number of available role cards so two cards sit
  // side-by-side and three sit in a row without crushing.
  const gridColumns = isSingleRole ? '1fr' : roleCount === 2 ? '1fr 1fr' : '1fr 1fr 1fr';

  return (
    <div style={styles.page}>
      <div style={styles.overlay} />
      <motion.div
        style={{
          ...styles.card,
          // Widen the card so three role cards have room to breathe.
          maxWidth: roleCount >= 3 ? '640px' : '520px',
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Inbox Branding */}
        <div style={styles.branding}>
          <div style={styles.cpLogo}>
            <svg width="40" height="40" viewBox="0 0 110 110" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="110" height="110" rx="22" fill="#0F4C75"/>
              <rect x="12" y="62" width="86" height="38" rx="6" fill="#1B9AAA" opacity="0.5"/>
              <rect x="12" y="82" width="86" height="18" rx="5" fill="#1B9AAA" opacity="0.4"/>
              <rect x="42" y="89" width="26" height="7" rx="3.5" fill="#F4A261"/>
              <rect x="24" y="18" width="62" height="72" rx="5" fill="white"/>
              <polygon points="70,18 86,18 86,34" fill="#0F4C75" opacity="0.2"/>
              <rect x="32" y="34" width="26" height="5" rx="2.5" fill="#F4A261"/>
              <rect x="32" y="46" width="38" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
              <rect x="32" y="55" width="32" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
              <rect x="32" y="64" width="28" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
            </svg>
            <h1 style={styles.cpTitle}>Inbox</h1>
          </div>
        </div>

        <h2 style={styles.greeting}>Welcome, {firstName}</h2>
        <p style={styles.subtitle}>
          {isSingleRole ? 'Tap your role to get started' : 'How would you like to sign in?'}
        </p>

        <div style={{
          ...styles.roleGrid,
          gridTemplateColumns: gridColumns,
          maxWidth: isSingleRole ? '240px' : undefined,
          margin: isSingleRole ? '0 auto 1.25rem' : undefined,
        }}>
          {roles.includes('adviser') && (
            <motion.button
              style={styles.roleCard}
              onClick={() => selectRole('adviser')}
              whileHover={{ scale: 1.03, boxShadow: '0 8px 24px rgba(15, 76, 117, 0.18)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div style={{ ...styles.roleIcon, background: 'rgba(15, 76, 117, 0.1)', color: 'var(--cp-primary)' }}>
                <Briefcase size={28} />
              </div>
              <h3 style={styles.roleName}>Adviser</h3>
              <p style={styles.roleDesc}>Pipeline overview, submit files, monitor SLA, settings</p>
            </motion.button>
          )}

          {roles.includes('assistant') && (
            <motion.button
              style={styles.roleCard}
              onClick={() => selectRole('assistant')}
              whileHover={{ scale: 1.03, boxShadow: '0 8px 24px rgba(27, 154, 170, 0.18)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div style={{ ...styles.roleIcon, background: 'rgba(27, 154, 170, 0.1)', color: 'var(--cp-secondary)' }}>
                <Inbox size={28} />
              </div>
              <h3 style={styles.roleName}>{getRoleLabel('assistant')}</h3>
              <p style={styles.roleDesc}>Draw files, process workflow, track bonus progress</p>
            </motion.button>
          )}

          {roles.includes('new_business_assistant') && (
            <motion.button
              style={styles.roleCard}
              onClick={() => selectRole('new_business_assistant')}
              whileHover={{ scale: 1.03, boxShadow: '0 8px 24px rgba(244, 162, 97, 0.18)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div style={{ ...styles.roleIcon, background: 'rgba(244, 162, 97, 0.1)', color: 'var(--cp-accent)' }}>
                <ClipboardList size={28} />
              </div>
              <h3 style={styles.roleName}>{getRoleLabel('new_business_assistant')}</h3>
              <p style={styles.roleDesc}>Preparation requests workspace (coming soon)</p>
            </motion.button>
          )}
        </div>

        {!isSingleRole && (
          <p style={styles.hint}>You can switch roles at any time from the header.</p>
        )}
      </motion.div>
    </div>
  );
}

const styles = {
  page: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, var(--cp-primary) 0%, var(--cp-secondary) 50%, var(--cp-primary) 100%)',
    position: 'relative',
    padding: '1rem',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse at 30% 20%, rgba(244, 162, 97, 0.15) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    background: 'rgba(255, 255, 255, 0.97)',
    borderRadius: '20px',
    padding: '2.5rem',
    maxWidth: '520px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
  },
  branding: {
    marginBottom: '1.5rem',
  },
  cpLogo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.6rem',
  },
  cpTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--cp-primary)',
    margin: 0,
    letterSpacing: '-0.02em',
  },
  greeting: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    margin: '0 0 0.25rem',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'var(--cp-text-muted)',
    margin: '0 0 1.5rem',
  },
  roleGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
    marginBottom: '1.25rem',
  },
  roleCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.6rem',
    padding: '1.5rem 1rem',
    background: 'var(--cp-surface)',
    border: '1.5px solid var(--cp-border)',
    borderRadius: '14px',
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  },
  roleIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    borderRadius: '14px',
  },
  roleName: {
    fontSize: '1rem',
    fontWeight: 700,
    color: 'var(--cp-text)',
    margin: 0,
  },
  roleDesc: {
    fontSize: '0.75rem',
    color: 'var(--cp-text-muted)',
    lineHeight: 1.4,
    margin: 0,
  },
  hint: {
    fontSize: '0.73rem',
    color: 'var(--cp-text-muted)',
    margin: 0,
  },
};
